import React from "react";
import { includes, intersection, map, partial } from "ramda";
import styled, { withTheme } from "styles/styled_components";

import { IThemeInterface } from "styles/theme";

import CheckboxWithLabel from "components/shared/checkbox_with_label";
import Icon, { ICON_COLOR, ICON_TYPE } from "components/shared/icon";
import useToggle from "helpers/hooks/useToggle";

const StyledSecondCheckboxTier = styled.div`
  padding-left: ${props => `${4.5 * props.theme.rawGridBase}px`};
`;

const StyledCategoryDropdown = styled.div`
  align-items: center;
  display: flex;
  margin-top: ${({ theme }) => `${0.5 * theme.rawGridBase}px`};
`;

interface IToggle {
  isExpanded: boolean;
}

const StyledToggle = styled("div")<IToggle>`
  padding-top: ${({ isExpanded, theme }) =>
    isExpanded ? `${0.5 * theme.rawGridBase}px` : 0};
  padding-left: ${({ theme }) => `${0.5 * theme.rawGridBase}px`};
  padding-right: ${({ theme }) => `${theme.rawGridBase}px`};
  transform: ${({ isExpanded }) => (isExpanded ? "" : "rotate(-90deg)")};
`;

interface ICategoryRoleList {
  categoryName: string;
  allRoles: string[];
  selectedRoles: string[];
  theme: IThemeInterface;
  toggleRole: (role: string) => void;
  toggleCategory: (category: string) => void;
}

function CategoryRoleList({
  allRoles,
  categoryName,
  selectedRoles,
  theme,
  toggleCategory,
  toggleRole,
}: ICategoryRoleList) {
  const [expanded, toggleExpanded] = useToggle(false);

  let roleOptions;
  if (expanded) {
    const roleChecks = map(role => {
      return (
        <CheckboxWithLabel
          label={role}
          key={role}
          checked={includes(role, selectedRoles)}
          onChangeHandler={partial(toggleRole, [role])}
        />
      );
    }, allRoles);
    roleOptions = (
      <StyledSecondCheckboxTier>{roleChecks}</StyledSecondCheckboxTier>
    );
  }

  const selectedRolesInCategory = intersection(allRoles, selectedRoles);
  // Category is selected if all its roles are selected
  const categorySelected = selectedRolesInCategory.length === allRoles.length;
  // Category is partially selected if at least one of its roles is selected
  const categoryPartiallySelected =
    !categorySelected && selectedRolesInCategory.length > 0;

  return (
    <>
      <StyledCategoryDropdown>
        <StyledToggle isExpanded={expanded} onClick={toggleExpanded}>
          <Icon
            color={ICON_COLOR.GRAY}
            type={ICON_TYPE.DOWN_ARROW}
            size={theme.iconSize.micro}
          />
        </StyledToggle>
        <CheckboxWithLabel
          label={categoryName || "Roles"}
          key={categoryName}
          checked={categorySelected}
          hasUncheckedChildren={categoryPartiallySelected}
          onChangeHandler={partial(toggleCategory, [categoryName])}
        />
      </StyledCategoryDropdown>
      {roleOptions}
    </>
  );
}

export default withTheme(CategoryRoleList);
